import "./RegionTree.css";
import React, { useState, useEffect, useRef, useContext } from "react";
import Tree from "rc-tree";
import myImage from "./region.svg";
import RegionNode from "./RegionNode";
import { XCircleblack } from "../../../../../Components/IconComponents";
import RegionRegistrationForm from "../../CreateRegion/RegionRegistrationForm";
import { findAllChildrenRegionsById } from "../../../../../Services/EndPoints";
import { postDataWithBody } from "../../../../../Services/Services";
import Modal from "react-bootstrap/Modal";
import RMMap from "../../RMMap/RMMap";
import { ThemeContext } from "../../../../..";

const RegionTree = () => {
  // State variables
  const [treeData, setTreeData] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [regionTypeId, setRegionTypeId] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([52]);
  const [actionType, setActionType] = useState("");
  const [keyVal, setKeyVal] = useState("");
  const [actionTypeHeader, setActionTypeHeader] = useState("");
  const [id, setId] = useState(null);
  const [districtId, setDistrictId] = useState("");
  const [selectedDistrictIds, setSelectedDistrictIds] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [selectedPanchayatIds, setSelectedPanchayatIds] = useState([]);
  const [selectedVillageIds, setSelectedVillageIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Store region data globally to access when checking
  const regionDataMap = useRef(new Map()).current;
  const componentRef = useRef();
  const { theme } = useContext(ThemeContext);

  // Helper function: Determine hierarchy level and if it's the last node
  const getHierarchyInfo = (key) => {
    let keySanitized = null;
    let level = 0;
    let isLastNode = false;
    
    if (key.indexOf("-") > -1) {
      const keyParts = key.split("-");
      level = keyParts.length - 1; // 1=District, 2=Block, 3=Panchayat, 4=Village
      
      // Village is the last level (level 4)
      if (keyParts.length === 5) { // Bihar-District-Block-Panchayat-Village
        isLastNode = true;
      }
      keySanitized = keyParts[keyParts.length - 1];
    } else {
      keySanitized = key;
      level = 0; // Bihar level
    }
    
    return { keySanitized, level, isLastNode };
  };

  // Helper function: Generate tree nodes
  const generateTreeNodes = async (treeNode) => {
    const arr = [];
    const key = treeNode.props.eventKey;
    const { keySanitized, level, isLastNode } = getHierarchyInfo(key);
    
    const rgnList = await postDataWithBody(findAllChildrenRegionsById, [keySanitized], {});
    
    rgnList.forEach(function (region) {
      let keyVal = `${key}-${region.id}`;
      
      // Store region data in the map with the key for later reference
      regionDataMap.set(keyVal, {
        name: region.name,
        id: region.id,
        region_type_id: region.region_type_id,
        gis_id: region.gis_id,
        level: level + 1
      });
      
      // Also store the data with just the ID as key for easy lookup
      regionDataMap.set(region.id.toString(), {
        name: region.name,
        id: region.id,
        region_type_id: region.region_type_id,
        gis_id: region.gis_id,
        level: level + 1
      });
      
      // Determine if this new node will be a leaf (Village level)
      const newLevel = level + 1;
      const willBeLeaf = newLevel >= 4; // Village level (4) is leaf
      
      arr.push({
        title: (
          <RegionNode
            regionTypeId={region.region_type_id}
            isLastNode={willBeLeaf}
            keyVal={keyVal}
            selectedId={handleSelectedId}
            id={region.id}
            text={region.name}
          />
        ),
        key: keyVal,
        isLeaf: willBeLeaf,
        checkable: true,
      });
    });

    return arr;
  };

  // Helper function: Handle selected ID
  const handleSelectedId = (idval, type, keyVal, regionTypeId) => {
    showModalPopup(idval, type, keyVal, regionTypeId);
  };

  // Helper function: Set leaf nodes
  const setLeaf = (treeData, curKey, level) => {
    const loopLeaf = (data, lev) => {
      const l = lev - 1;
      data.forEach((item) => {
        if (
          item.key.length > curKey.length
            ? item.key.indexOf(curKey) !== 0
            : curKey.indexOf(item.key) !== 0
        ) {
          return;
        }
        if (item.children) {
          loopLeaf(item.children, l);
        } else if (l < 1) {
          item.isLeaf = true;
        }
      });
    };
    loopLeaf(treeData, level + 1);
  };

  // Helper function: Get new tree data
  const getNewTreeData = (treeData, curKey, child, level) => {
    const loop = (data) => {
      if (level < 1) return;
      data.forEach((item) => {
        if (curKey.indexOf(item.key) === 0) {
          if (item.children) {
            loop(item.children);
          } else {
            item.children = child;
          }
        }
      });
    };
    loop(treeData);
    setLeaf(treeData, curKey, level);
  };

  // Helper function: Get all child keys recursively
  const getAllChildKeys = (treeData, parentKey) => {
    const childKeys = [];
    
    const findChildren = (nodes) => {
      nodes.forEach(node => {
        if (node.key.startsWith(parentKey + '-')) {
          childKeys.push(node.key);
          if (node.children) {
            findChildren(node.children);
          }
        }
      });
    };
    
    findChildren(treeData);
    return childKeys;
  };

  // Helper function: Categorize regions by level
  const categorizeRegionsByLevel = (regions) => {
    const categorized = {
      districts: [],
      blocks: [],
      panchayats: [],
      villages: []
    };
    
    regions.forEach(region => {
      if (region.level === 1) categorized.districts.push(region);
      else if (region.level === 2) categorized.blocks.push(region);
      else if (region.level === 3) categorized.panchayats.push(region);
      else if (region.level === 4) categorized.villages.push(region);
    });
    
    return categorized;
  };

  // Modal handlers
  const showModalPopup = (id, type, keyVal, regionTypeId) => {
    let actionTypeStr = type === "EDIT" ? "Edit Region Information" : "Create Region";
    setRegionTypeId(regionTypeId);
    setShowPopup(true);
    setActionType(type);
    setKeyVal(keyVal);
    setActionTypeHeader(actionTypeStr);
    setId(id);
  };

  const hideModalPopup = () => {
    setShowPopup(false);
  };

  // Initialize component
  useEffect(() => {
    window.regionTreeComponent = {
      showModalPopup,
      hideModalPopup
    };
    
    // Add Bihar to the regionDataMap
    regionDataMap.set("52", {
      name: "BIHAR",
      id: 52,
      region_type_id: null,
      gis_id: null,
      level: 0
    });
    
    const timer = setTimeout(() => {
      setTreeData([{ 
        title: "BIHAR", 
        key: "52",
        checkable: true
      }]);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Event handlers
  const onSelect = (info) => {
    console.log("selected", info);
  };

const onCheck = async (checkedKeys, info) => {
  setIsLoading(true);
  console.log("Checked keys:", checkedKeys);
  console.log("Check info:", info);
  
  const node = info.node;
  const nodeKey = node.key;
  const regionData = regionDataMap.get(nodeKey);
  
  // Get all keys that are currently checked
  const currentCheckedKeys = Array.isArray(checkedKeys) ? 
    checkedKeys : 
    checkedKeys.checked || [];
  
  let newCheckedKeys = [...currentCheckedKeys];
  let newSelectedRegions = [...selectedRegions];
  let newSelectedDistrictIds = [...selectedDistrictIds];
  let newSelectedBlockIds = [...selectedBlockIds];
  let newSelectedPanchayatIds = [...selectedPanchayatIds];
  let newSelectedVillageIds = [...selectedVillageIds];

  try {
    if (regionData) {
      if (info.checked) {
        // Node is being checked
        if (!currentCheckedKeys.includes(nodeKey)) {
          newCheckedKeys.push(nodeKey);
        }

        // Special handling for Bihar node (select all districts)
        if (nodeKey === "52") {
          // First ensure we have all child data loaded
          if (!treeData.find(item => item.key === "52")?.children) {
            // If children aren't loaded yet, force load them
            const biharChildren = await generateTreeNodes({ props: { eventKey: "52" } });
            const newTreeData = [...treeData];
            getNewTreeData(newTreeData, "52", biharChildren, 5);
            setTreeData(newTreeData);
            
            // Now process all districts
            const allDistrictKeys = biharChildren.map(child => child.key);
            newCheckedKeys = [...new Set([...newCheckedKeys, ...allDistrictKeys])];
            
            // Add all district GIS IDs to selectedDistrictIds
            const allDistrictGisIds = [];
            biharChildren.forEach(child => {
              const childData = regionDataMap.get(child.key);
              if (childData && childData.gis_id) {
                allDistrictGisIds.push(childData.gis_id);
                newSelectedRegions.push({
                  name: childData.name,
                  gis_id: childData.gis_id,
                  id: childData.id,
                  region_type_id: childData.region_type_id,
                  level: childData.level
                });
              }
            });
            
            newSelectedDistrictIds = [...new Set([...newSelectedDistrictIds, ...allDistrictGisIds])];
          } else {
            // Children are already loaded, process them
            const biharChildren = treeData.find(item => item.key === "52")?.children || [];
            const allDistrictKeys = biharChildren.map(child => child.key);
            newCheckedKeys = [...new Set([...newCheckedKeys, ...allDistrictKeys])];
            
            // Add all district GIS IDs to selectedDistrictIds
            const allDistrictGisIds = [];
            biharChildren.forEach(child => {
              const childData = regionDataMap.get(child.key);
              if (childData && childData.gis_id) {
                allDistrictGisIds.push(childData.gis_id);
                newSelectedRegions.push({
                  name: childData.name,
                  gis_id: childData.gis_id,
                  id: childData.id,
                  region_type_id: childData.region_type_id,
                  level: childData.level
                });
              }
            });
            
            newSelectedDistrictIds = [...new Set([...newSelectedDistrictIds, ...allDistrictGisIds])];
          }
        } else {
          // Handle individual nodes
          await processIndividualNode(nodeKey, regionData, newCheckedKeys, newSelectedRegions,
            newSelectedDistrictIds, newSelectedBlockIds, newSelectedPanchayatIds, newSelectedVillageIds, true);
          
          // Check if we need to auto-check Bihar state
          if (regionData.level === 1) { // District level
            const biharChildren = treeData.find(item => item.key === "52")?.children || [];
            const allDistrictKeys = biharChildren.map(child => child.key);
            
            // Check if all districts are now selected
            const allDistrictsSelected = allDistrictKeys.length > 0 && 
              allDistrictKeys.every(districtKey => newCheckedKeys.includes(districtKey));
            
            // If all districts are selected and Bihar is not already checked, check Bihar
            if (allDistrictsSelected && !newCheckedKeys.includes("52")) {
              newCheckedKeys.push("52");
            }
          }
        }
      } else {
        // Node is being unchecked
        if (nodeKey === "52") {
          // Get all descendant keys recursively
          const getAllDescendantKeys = (nodes, parentKey) => {
            const descendants = [];
            const processNodes = (nodeList) => {
              nodeList.forEach(node => {
                if (node.key.startsWith(parentKey + '-') || node.key === parentKey) {
                  descendants.push(node.key);
                  if (node.children) {
                    processNodes(node.children);
                  }
                }
              });
            };
            processNodes(nodes);
            return descendants;
          };
          
          // Get all Bihar descendants
          const biharDescendants = getAllDescendantKeys(treeData, "52");
          
          // Remove Bihar and all its descendants from checked keys
          newCheckedKeys = newCheckedKeys.filter(key => 
            !biharDescendants.includes(key) && key !== "52"
          );
          
          // Clear all selected regions and IDs
          newSelectedRegions = [];
          newSelectedDistrictIds = [];
          newSelectedBlockIds = [];
          newSelectedPanchayatIds = [];
          newSelectedVillageIds = [];
        } else {
          await processIndividualNode(nodeKey, regionData, newCheckedKeys, newSelectedRegions,
            newSelectedDistrictIds, newSelectedBlockIds, newSelectedPanchayatIds, newSelectedVillageIds, false);
          
          // Check if the unchecked node is a district and Bihar is currently checked
          if (regionData.level === 1 && newCheckedKeys.includes("52")) {
            // Automatically uncheck Bihar when any district is unchecked
            newCheckedKeys = newCheckedKeys.filter(key => key !== "52");
          }
        }
      }
    }
    
    // Update all states
    setCheckedKeys(newCheckedKeys);
    setSelectedRegions(newSelectedRegions);
    setSelectedDistrictIds(newSelectedDistrictIds);
    setSelectedBlockIds(newSelectedBlockIds);
    setSelectedPanchayatIds(newSelectedPanchayatIds);
    setSelectedVillageIds(newSelectedVillageIds);
    setDistrictId(regionData?.gis_id || "");
  } finally {
    setIsLoading(false);
  }
};

  // Helper function: Process children recursively
  const processChildrenRecursively = async (children, checkedKeys, regions, districts, blocks, panchayats, villages) => {
    for (const child of children) {
      if (!checkedKeys.includes(child.key)) {
        checkedKeys.push(child.key);
        const childData = regionDataMap.get(child.key);
        
        if (childData) {
          // Add to regions array
          regions.push({
            name: childData.name,
            gis_id: childData.gis_id,
            id: childData.id,
            region_type_id: childData.region_type_id,
            level: childData.level
          });
          
          // Add to appropriate category based on level
          if (childData.gis_id) {
            switch (childData.level) {
              case 1: // District
                districts.push(childData.gis_id);
                break;
              case 2: // Block
                blocks.push(childData.gis_id);
                break;
              case 3: // Panchayat
                panchayats.push(childData.gis_id);
                break;
              case 4: // Village
                villages.push(childData.gis_id);
                break;
            }
          }
        }
        
        // Process grandchildren if they exist
        if (child.children) {
          await processChildrenRecursively(child.children, checkedKeys, regions, districts, blocks, panchayats, villages);
        }
      }
    }
  };

  // Helper function: Process individual node
  const processIndividualNode = async (nodeKey, regionData, checkedKeys, regions, districts, blocks, panchayats, villages, isChecking) => {
    if (isChecking) {
      // Add current node
      if (regionData.gis_id) {
        switch (regionData.level) {
          case 1: // District
            districts.push(regionData.gis_id);
            break;
          case 2: // Block
            blocks.push(regionData.gis_id);
            break;
          case 3: // Panchayat
            panchayats.push(regionData.gis_id);
            break;
          case 4: // Village
            villages.push(regionData.gis_id);
            break;
        }
      }
      
      regions.push({
        name: regionData.name,
        gis_id: regionData.gis_id,
        id: regionData.id,
        region_type_id: regionData.region_type_id,
        level: regionData.level
      });

      // Also check all children if this is not a leaf node
      if (regionData.level < 4) {
        const allChildKeys = getAllChildKeys(treeData, nodeKey);
        for (const childKey of allChildKeys) {
          if (!checkedKeys.includes(childKey)) {
            checkedKeys.push(childKey);
            const childData = regionDataMap.get(childKey);
            if (childData) {
              regions.push({
                name: childData.name,
                gis_id: childData.gis_id,
                id: childData.id,
                region_type_id: childData.region_type_id,
                level: childData.level
              });
              
              if (childData.gis_id) {
                switch (childData.level) {
                  case 1: districts.push(childData.gis_id); break;
                  case 2: blocks.push(childData.gis_id); break;
                  case 3: panchayats.push(childData.gis_id); break;
                  case 4: villages.push(childData.gis_id); break;
                }
              }
            }
          }
        }
      }
    } else {
      // Unchecking - remove current node and all children
      const keysToRemove = [nodeKey, ...getAllChildKeys(treeData, nodeKey)];
      
      for (const keyToRemove of keysToRemove) {
        // Remove from checked keys
        const index = checkedKeys.indexOf(keyToRemove);
        if (index > -1) {
          checkedKeys.splice(index, 1);
        }
        
        // Remove from regions and category arrays
        const dataToRemove = regionDataMap.get(keyToRemove);
        if (dataToRemove) {
          // Remove from regions
          const regionIndex = regions.findIndex(r => r.id === dataToRemove.id);
          if (regionIndex > -1) {
            regions.splice(regionIndex, 1);
          }
          
          // Remove from appropriate category
          if (dataToRemove.gis_id) {
            switch (dataToRemove.level) {
              case 1:
                removeFromArray(districts, dataToRemove.gis_id);
                break;
              case 2:
                removeFromArray(blocks, dataToRemove.gis_id);
                break;
              case 3:
                removeFromArray(panchayats, dataToRemove.gis_id);
                break;
              case 4:
                removeFromArray(villages, dataToRemove.gis_id);
                break;
            }
          }
        }
      }
    }
  };

  // Helper function: Remove item from array
  const removeFromArray = (array, item) => {
    const index = array.indexOf(item);
    if (index > -1) {
      array.splice(index, 1);
    }
  };

  const onLoadData = (treeNode) => {
    console.log("load data...");
    return new Promise(async (resolve, reject) => {
      const newTreeData = [...treeData];
      let regionList = await generateTreeNodes(treeNode);
      getNewTreeData(newTreeData, treeNode.props.eventKey, regionList, 5); // Increased level to accommodate villages
      setTreeData(newTreeData);
      resolve();
    });
  };

  return (
    <div className="tree_container mt-2 d-flex srollable-hidden" style={{ height: "375pt" }}>
      <div className="col-sm-4 tree_div">
        <Tree
          onSelect={onSelect}
          onCheck={onCheck}
          loadData={onLoadData}
          treeData={treeData}
          checkable={true}
          checkedKeys={checkedKeys}
          checkStrictly={true}
          showLine={true}
        />
      </div>
      <div className="image_div map-sticky" style={{ overflow: "hidden"}}>  
        <RMMap
          selectedRegions={selectedRegions}
          selectedDistrictIds={selectedDistrictIds}
          selectedBlockIds={selectedBlockIds}
          selectedPanchayatIds={selectedPanchayatIds}
          selectedVillageIds={selectedVillageIds}
          selectedDistrictId={districtId}
          isLoading={isLoading}
        />
      </div>

      {showPopup && (
        <Modal
          show={showPopup}
          size="lg"
          className={theme === "light" ? "table__modal__popup-light" : "table__modal__popup"}
          centered>
          <Modal.Header>
            <Modal.Title>{actionTypeHeader}</Modal.Title>
            <XCircleblack
              className="cursor-pointer"
              onClick={hideModalPopup}
              width="1.5em"
              height="1.5em"
            />
          </Modal.Header>
          <Modal.Body>
            <div>
              <RegionRegistrationForm
                regionTypeId={regionTypeId}
                id={id}
                keyVal={keyVal}
                actionType={actionType}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            {/* Footer content preserved from original */}
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default RegionTree;